import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { RippCore } from "./v2/RippCore";
import { CoreVideo } from "./universe/CoreVideo";

/* ENTER THE RIPP UNIVERSE — live canvas hero (no video, no static image).
   Timeline: darkness → energy pulse → liquid market stream → candles form → agent nodes wake → Core emerges → RIPP.
   Reacts to cursor + time; SIMULATION-labelled synthetic price path; reduced-motion + lite fallbacks. */

const AGENTS = ["MARKET STRUCTURE", "MOMENTUM", "LIQUIDITY", "MACRO", "RISK", "TRADE MGMT", "POSITION MGMT", "EXECUTION"];

export const UniverseHero = () => {
  const ref = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.5 });
  const [phase, setPhase] = useState(0);            // 0 dark · 1 stream · 2 candles · 3 agents · 4 core · 5 title
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const lite = typeof navigator !== "undefined" && (navigator.hardwareConcurrency || 8) <= 4;

  useEffect(() => {
    const cv = ref.current, ctx = cv.getContext("2d");
    const dpr = lite ? 1 : Math.min(2, window.devicePixelRatio || 1);
    const fit = () => { cv.width = cv.clientWidth * dpr; cv.height = cv.clientHeight * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); };
    fit(); window.addEventListener("resize", fit);
    const onMove = (e) => { mouse.current = { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight }; };
    window.addEventListener("mousemove", onMove);
    // synthetic random-walk path (labelled SIMULATION) — never presented as live data
    let seed = 7; const rnd = () => (seed = (seed * 9301 + 49297) % 233280) / 233280;
    const N = lite ? 90 : 160; const path = [100];
    for (let i = 1; i < N; i++) path.push(path[i - 1] + (rnd() - 0.47) * 1.4 + Math.sin(i / 11) * 0.35);
    const lo = Math.min(...path), hi = Math.max(...path);
    const t0 = performance.now(); let raf;
    const draw = () => {
      const t = reduced ? 9 : (performance.now() - t0) / 1000;
      const W = cv.clientWidth, H = cv.clientHeight;
      const ph = t < 1 ? 0 : t < 3 ? 1 : t < 5 ? 2 : t < 6.5 ? 3 : t < 8 ? 4 : 5;
      setPhase((p) => (p === ph ? p : ph));
      ctx.clearRect(0, 0, W, H);
      const mx = (mouse.current.x - 0.5), my = (mouse.current.y - 0.5);
      // energy pulse
      const pr = Math.min(1, t / 1.2);
      const g = ctx.createRadialGradient(W * 0.5 + mx * 40, H * 0.5 + my * 30, 0, W * 0.5, H * 0.5, W * 0.45 * pr);
      g.addColorStop(0, `rgba(56,189,248,${0.10 * pr})`); g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      // liquid market stream
      const reveal = Math.max(0, Math.min(1, (t - 1) / 3.2));
      const y = (v) => H * 0.62 - ((v - lo) / (hi - lo)) * H * 0.34 + my * 18;
      const x = (i) => (i / (N - 1)) * W * 1.05 - W * 0.02 + mx * 24;
      const shown = Math.floor(N * reveal);
      if (shown > 2) {
        for (let k = 3; k >= 0; k--) {                                   // layered flowing river
          ctx.beginPath();
          for (let i = 0; i < shown; i++) {
            const wob = Math.sin(t * 1.6 + i * 0.25 + k) * (2 + k * 2.2) * (1 - k * 0.15);
            const yy = y(path[i]) + wob + k * 9;
            i ? ctx.lineTo(x(i), yy) : ctx.moveTo(x(i), yy);
          }
          ctx.strokeStyle = `rgba(56,189,248,${(0.55 - k * 0.12) * reveal})`; ctx.lineWidth = k ? 1 : 1.8; ctx.stroke();
        }
        // data particles travelling along the stream
        if (!reduced) for (let p = 0; p < (lite ? 12 : 28); p++) {
          const f = ((t * 0.12 + p / 28) % 1) * (shown - 1), i = Math.floor(f);
          ctx.fillStyle = `rgba(200,240,255,${0.7 * reveal})`; ctx.beginPath(); ctx.arc(x(i), y(path[i]) + Math.sin(t * 1.6 + i * 0.25) * 2, 1.4, 0, Math.PI * 2); ctx.fill();
        }
      }
      // candles form from the stream
      const cr = Math.max(0, Math.min(1, (t - 3) / 2.2));
      if (cr > 0) {
        const step = 4, cw = Math.max(2, (W / N) * step * 0.55);
        for (let i = step; i < Math.floor(shown * cr); i += step) {
          const o = path[i - step], c = path[i], up = c >= o;
          const hh = Math.max(o, c) + 0.3, ll = Math.min(o, c) - 0.3;
          ctx.strokeStyle = up ? "rgba(0,229,181,0.75)" : "rgba(255,51,85,0.75)"; ctx.lineWidth = 1;
          ctx.beginPath(); ctx.moveTo(x(i), y(hh)); ctx.lineTo(x(i), y(ll)); ctx.stroke();
          ctx.fillStyle = up ? "rgba(0,229,181,0.55)" : "rgba(255,51,85,0.55)";
          ctx.fillRect(x(i) - cw / 2, y(Math.max(o, c)), cw, Math.max(1.5, Math.abs(y(o) - y(c))));
        }
        // structure: swing highs/lows + a liquidity pool line
        ctx.setLineDash([6, 5]); ctx.strokeStyle = `rgba(245,185,66,${0.5 * cr})`;
        ctx.beginPath(); ctx.moveTo(W * 0.15, y(hi - 0.4)); ctx.lineTo(W * 0.9, y(hi - 0.4)); ctx.stroke(); ctx.setLineDash([]);
        ctx.fillStyle = `rgba(245,185,66,${0.8 * cr})`; ctx.font = "700 9px JetBrains Mono, monospace"; ctx.fillText("BUY-SIDE LIQUIDITY", W * 0.15, y(hi - 0.4) - 5);
      }
      // agent nodes wake up around the core position
      const ar = Math.max(0, Math.min(1, (t - 5) / 1.5));
      const cx = W * 0.5 + mx * 30, cy = H * 0.42 + my * 20, R = Math.min(W, H) * 0.3;
      if (ar > 0) AGENTS.forEach((n, i) => {
        if (i / AGENTS.length > ar) return;
        const a = (i / AGENTS.length) * Math.PI * 2 - Math.PI / 2 + t * 0.05;
        const ax = cx + Math.cos(a) * R, ay = cy + Math.sin(a) * R * 0.62;
        ctx.strokeStyle = `rgba(56,189,248,${0.25 * ar})`; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(cx, cy); ctx.stroke();
        const pulse = 0.5 + 0.5 * Math.sin(t * 2 + i);
        ctx.fillStyle = `rgba(125,211,252,${0.6 + 0.4 * pulse})`; ctx.beginPath(); ctx.arc(ax, ay, 2.5 + pulse, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = `rgba(127,139,163,${ar})`; ctx.font = "700 8px JetBrains Mono, monospace"; ctx.textAlign = ax < cx ? "right" : "left";
        ctx.fillText(n, ax + (ax < cx ? -8 : 8), ay + 3); ctx.textAlign = "left";
      });
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", fit); window.removeEventListener("mousemove", onMove); };
  }, [lite, reduced]);

  return (
    <section className="relative h-screen min-h-[640px] w-full overflow-hidden bg-[#050A14]" data-testid="universe-hero" data-phase={phase}>
      <div className={`pointer-events-none absolute inset-0 transition-opacity duration-[2500ms] ${phase >= 2 ? "opacity-40" : "opacity-0"}`} data-testid="universe-video-layer">
        <CoreVideo className="h-full w-full object-cover" testid="universe-video" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(5,10,20,0.35)_0%,rgba(5,10,20,0.85)_75%)]" />
      </div>
      <canvas ref={ref} className="absolute inset-0 h-full w-full" />
      <div className={`absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2 transition-opacity duration-[1500ms] ${phase >= 4 ? "opacity-100" : "opacity-0"}`} data-testid="universe-core">
        <RippCore mode={phase >= 5 ? "analyzing" : "scanning"} agents={[]} size={lite ? 220 : 300} />
      </div>
      <div className={`absolute inset-x-0 top-[66%] text-center transition-all duration-[1400ms] ${phase >= 5 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-[0.22em] text-white" data-testid="universe-title">RIPP</h1>
        <p className="mt-2 text-base md:text-lg font-black tracking-[0.18em] text-[#38BDF8]">BUILD YOUR STRATEGY. LET IT RIPP.</p>
        <p className="mt-3 text-[10px] md:text-xs font-mono tracking-[0.28em] text-[#7F8BA3]">AN AUTONOMOUS MARKET INTELLIGENCE SYSTEM<br />BUILT TO SEE, THINK, MANAGE AND ADAPT.</p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Link to="/v2" data-testid="universe-enter" className="rounded-sm bg-[#38BDF8] px-6 py-2.5 text-[11px] font-black tracking-[0.2em] text-[#041018] shadow-[0_0_30px_rgba(56,189,248,0.45)] transition-transform hover:scale-[1.03]">ENTER RIPP</Link>
          <Link to="/v2/demo" data-testid="universe-watch" className="rounded-sm border border-[#38BDF8]/50 px-6 py-2.5 text-[11px] font-black tracking-[0.2em] text-[#38BDF8] hover:bg-[#38BDF8]/10">WATCH THE SYSTEM THINK</Link>
        </div>
      </div>
      <div className="absolute bottom-3 left-4 text-[8px] font-mono tracking-[0.24em] text-[#7F8BA3]" data-testid="universe-data-label">MARKET STREAM · SIMULATION</div>
      <div className="absolute bottom-3 right-4 text-[8px] font-mono tracking-[0.24em] text-[#7F8BA3] animate-pulse">SCROLL TO ENTER THE CORE ↓</div>
    </section>
  );
};
